import { createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { AdminCourseNotFound } from "@/features/admin/components/AdminCourseNotFound";
import { EditCourseLayout } from "@/features/admin/edit-course/EditCourseLayout";
import {
  resolveEditCourseTab,
  type EditCourseTabId,
  validateEditCourseSearch,
} from "@/features/admin/edit-course/editCourseTabs";
import { isInstructorLed } from "@/features/admin/instructor-led/instructorLed";
import { AvailabilityPanel } from "@/features/admin/edit-course/panels/AvailabilityPanel";
import { ContentAndVersionsPanel } from "@/features/admin/edit-course/panels/ContentAndVersionsPanel";
import { DetailsPanel } from "@/features/admin/edit-course/panels/DetailsPanel";
import { SessionsPanel } from "@/features/admin/edit-course/panels/SessionsPanel";
import { toastSuccess } from "@/utils/toast";
import { adminGetCourse } from "@/services/admin/courses";
import type { CourseDetailDto } from "@/features/admin/types";
import { useCourseIngestionUpdates } from "@/features/admin/edit-course/useCourseIngestionUpdates";
import type { CoursesFlashState } from "./-flashState";

export const Route = createFileRoute("/admin/courses/$courseId")({
  component: EditCoursePage,
  validateSearch: validateEditCourseSearch,
  remountDeps: ({ params }) => params.courseId,
});

/**
 * Loads the course and streams ingestion progress into its query cache. Each URL-backed tab panel
 * owns its edits and blocks navigation while they are unsaved.
 */
function EditCoursePage() {
  const { courseId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const routerState = useRouterState();

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "courses", courseId],
    queryFn: ({ signal }) => adminGetCourse(courseId, signal),
    retry: (failureCount, error) => {
      // Don't retry on 404 errors
      if (isAxiosError(error) && error.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  }) as { data: CourseDetailDto | undefined; isLoading: boolean; error: Error | null };

  useEffect(() => {
    const incomingFlash = (routerState.location.state as unknown as CoursesFlashState | undefined)
      ?.flash;
    // Leaving with a flash, e.g. after deleting the course, updates the location before this page
    // unmounts. That flash is for the next page, which shows it.
    if (!incomingFlash || routerState.location.pathname !== `/admin/courses/${courseId}`) {
      return;
    }

    toastSuccess(incomingFlash.message, "Your changes have been saved successfully.");

    navigate({
      to: "/admin/courses/$courseId",
      params: { courseId },
      replace: true,
      search: { tab },
      state: undefined,
    });
  }, [courseId, navigate, routerState.location.pathname, routerState.location.state, tab]);

  useCourseIngestionUpdates(courseId, data);

  // Check for errors first (before loading check) to handle 404s properly
  if (error) {
    const isNotFoundError = isAxiosError(error) && error.response?.status === 404;
    if (isNotFoundError) {
      return (
        <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["Admin"]}>
          <AdminCourseNotFound />
        </ProtectedRoute>
      );
    }
    return (
      <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["Admin"]}>
        <AdminPageLayout mainClassName="bg-secondary_alt">
          <div className="bg-secondary_alt flex min-h-[60vh] items-center justify-center">
            <p className="text-sm text-red-600">Unable to load course details.</p>
          </div>
        </AdminPageLayout>
      </ProtectedRoute>
    );
  }

  if (isLoading || !data) {
    return (
      <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["Admin"]}>
        <AdminPageLayout mainClassName="bg-secondary_alt">
          <div className="bg-secondary_alt flex min-h-[60vh] items-center justify-center">
            <p className="text-tertiary text-sm">Loading course details…</p>
          </div>
        </AdminPageLayout>
      </ProtectedRoute>
    );
  }

  // An instructor-led course has no Content and versions tab, so it opens on Details.
  const selectedTab = resolveEditCourseTab(tab, isInstructorLed(data));

  // Switching tabs is a navigation, so the Details tab's unsaved changes prompt can stop it.
  const handleTabChange = (nextTab: EditCourseTabId) => {
    void navigate({
      to: "/admin/courses/$courseId",
      params: { courseId },
      search: { tab: nextTab },
    });
  };

  return (
    <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["Admin"]}>
      <AdminPageLayout mainClassName="bg-secondary_alt">
        <EditCourseLayout
          course={data}
          selectedTab={selectedTab}
          onTabChange={handleTabChange}
          renderPanel={(panelTab) => {
            switch (panelTab) {
              case "details":
                return <DetailsPanel courseId={courseId} course={data} />;
              case "content":
                return <ContentAndVersionsPanel courseId={courseId} course={data} />;
              case "sessions":
                return <SessionsPanel courseId={courseId} course={data} />;
              case "availability":
                return <AvailabilityPanel courseId={courseId} course={data} />;
            }
          }}
        />
      </AdminPageLayout>
    </ProtectedRoute>
  );
}
